import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiResponse } from '../config/api.config';
import { CreditApplication, CreditMemo } from '../models/credit-memo.model';
import { ApiStore, newestFirst } from './api-store';
import { SalesOrderService } from './sales-order.service';

export type CreateCreditMemoInput = Omit<CreditMemo, 'id' | 'number' | 'applications'>;

/**
 * Customer credits (/credit-memos). A credit originates from a sales order and
 * its balance can be applied to any outstanding invoice of the same customer.
 */
@Injectable({ providedIn: 'root' })
export class CreditMemoService extends ApiStore<CreditMemo> {
  protected readonly resource = 'credit-memos';
  protected override readonly order = newestFirst<CreditMemo>((c) => c.requestedDate);
  protected override readonly readAbility = 'read.creditMemos' as const;
  private readonly salesOrderService = inject(SalesOrderService);

  create(input: CreateCreditMemoInput): Observable<CreditMemo> {
    return this.save(this.http.post<ApiResponse<CreditMemo>>(this.url, input));
  }

  /** Applies part (or all) of a credit's remaining balance against an invoice. */
  applyCredit(creditId: string, application: CreditApplication): Observable<CreditMemo> {
    return this.save(
      this.http
        .post<ApiResponse<CreditMemo>>(`${this.url}/${creditId}/apply`, application)
        .pipe(tap(() => this.salesOrderService.refresh())),
    );
  }

  /** "Unapply credit" — removes the application, releasing that amount back to the credit. */
  unapplyCredit(creditId: string, salesOrderId: string): Observable<CreditMemo> {
    return this.save(
      this.http
        .delete<ApiResponse<CreditMemo>>(`${this.url}/${creditId}/applications/${salesOrderId}`)
        .pipe(tap(() => this.salesOrderService.refresh())),
    );
  }
}
